import React from "react";
import PageShell from "@/components/layout/PageShell";
import PageHeader from "@/components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { createRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

const baseSchema = z.object({
  full_name: z.string().min(2),
  email: z.string().email(),
  phone: z.string().optional(),
  field1: z.string().optional(),
  field2: z.string().optional(),
  field3: z.string().optional(),
  notes: z.string().optional(),
});

export const GenericServicePage = ({
  serviceType,
  i18nKey,
  testIdPrefix,
  fieldLabels,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const { toast } = useToast();

  const form = useForm({
    resolver: zodResolver(baseSchema),
    defaultValues: {
      full_name: "",
      email: "",
      phone: "",
      field1: "",
      field2: "",
      field3: "",
      notes: "",
    },
  });

  const { register, handleSubmit, reset } = form;

  const onSubmit = async (values) => {
    try {
      const payload = {
        language: lang,
        service_type: serviceType,
        contact: {
          full_name: values.full_name,
          email: values.email,
          phone: values.phone,
        },
        meta: {
          field1: values.field1,
          field2: values.field2,
          field3: values.field3,
          notes: values.notes,
        },
      };
      await createRequest(payload);
      toast({ title: t(`${i18nKey}.success`) });
      reset();
    } catch (err) {
      console.error(err);
      toast({
        title: "Erro ao enviar pedido",
        description: "Tente novamente mais tarde.",
        variant: "destructive",
      });
    }
  };

  return (
    <PageShell>
      <PageHeader
        title={t(`${i18nKey}.pageTitle`)}
        subtitle={t(`${i18nKey}.pageSubtitle`)}
        testIdPrefix={`${testIdPrefix}-header`}
      />
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="max-w-3xl space-y-6"
        data-testid={`${testIdPrefix}-form`}
      >
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="full_name">Nome completo</Label>
            <Input
              id="full_name"
              {...register("full_name")}
              className="mt-1 bg-slate-900 border-slate-800"
              data-testid={`${testIdPrefix}-full-name-input`}
            />
          </div>
          <div>
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              {...register("email")}
              className="mt-1 bg-slate-900 border-slate-800"
              data-testid={`${testIdPrefix}-email-input`}
            />
          </div>
          <div>
            <Label htmlFor="phone">Telefone / WhatsApp</Label>
            <Input
              id="phone"
              {...register("phone")}
              className="mt-1 bg-slate-900 border-slate-800"
              data-testid={`${testIdPrefix}-phone-input`}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="field1">{fieldLabels.field1}</Label>
            <Input
              id="field1"
              {...register("field1")}
              className="mt-1 bg-slate-900 border-slate-800"
              data-testid={`${testIdPrefix}-field1-input`}
            />
          </div>
          <div>
            <Label htmlFor="field2">{fieldLabels.field2}</Label>
            <Input
              id="field2"
              {...register("field2")}
              className="mt-1 bg-slate-900 border-slate-800"
              data-testid={`${testIdPrefix}-field2-input`}
            />
          </div>
          <div>
            <Label htmlFor="field3">{fieldLabels.field3}</Label>
            <Input
              id="field3"
              {...register("field3")}
              className="mt-1 bg-slate-900 border-slate-800"
              data-testid={`${testIdPrefix}-field3-input`}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="notes">{t(`${i18nKey}.notes`)}</Label>
          <Textarea
            id="notes"
            rows={4}
            {...register("notes")}
            className="mt-1 bg-slate-900 border-slate-800 resize-none"
            data-testid={`${testIdPrefix}-notes-textarea`}
          />
        </div>

        <Button
          type="submit"
          className="rounded-full brand-gradient text-white hover:opacity-90"
          data-testid={`${testIdPrefix}-submit-button`}
        >
          {t(`${i18nKey}.submit`)}
        </Button>
      </form>
    </PageShell>
  );
};

export default GenericServicePage;
